import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Add,
  ArrowBack,
  PlaylistAddCheck,
  CheckCircle,
  Error as ErrorIcon,
  YouTube,
  People,
  VideoLibrary,
  Delete as DeleteIcon,
  Translate,
  Close,
  ContentCopy,
  Analytics,
  Star,
  StarBorder,
  Folder,
  Cancel,
} from '@mui/icons-material';
import {
  StyledContainer,
  StyledPaper,
  StyledSectionHeaderWithBadge,
  StyledAnalyticsIconBox,
  StyledChannelChip,
  StyledTextField,
  StyledEmptyState,
  StyledEmptyStateIcon,
} from './styles';
import VideoCard from './VideoCard';
import BulkAnalyzeModal from './BulkAnalyzeModal';
import { API_URL } from './config';
import axios from 'axios';
import { trackEvent } from './analytics';

// Using centralized API_URL from config

function FolderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [folder, setFolder] = useState(null);
  const [channelCount, setChannelCount] = useState(0);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalVideos, setTotalVideos] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [channelInput, setChannelInput] = useState('');
  const [addingChannels, setAddingChannels] = useState(false);
  const [statusMessages, setStatusMessages] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [channelToRemove, setChannelToRemove] = useState(null);
  const [removingChannel, setRemovingChannel] = useState(false);
  // Initialize all filter states from URL params
  const [sortBy, setSortBy] = useState(() => searchParams.get('sortBy') || 'viewCount');
  const [sortOrder, setSortOrder] = useState(() => searchParams.get('sortOrder') || 'desc');
  const [dateFilter, setDateFilter] = useState(() => searchParams.get('dateFilter') || 'week');
  const [query, setQuery] = useState(() => searchParams.get('q') || '');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showTranslation, setShowTranslation] = useState(() => {
    const translationParam = searchParams.get('translation');
    return translationParam !== null ? translationParam === 'true' : true;
  });
  const [channelsModalOpen, setChannelsModalOpen] = useState(false);
  const [channels, setChannels] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [channelStatusFilter, setChannelStatusFilter] = useState('all');
  const [channelSortBy, setChannelSortBy] = useState('viewsLast3Days');
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
  const [bulkAnalyzeModalOpen, setBulkAnalyzeModalOpen] = useState(false);
  const [showBulkAnalyze, setShowBulkAnalyze] = useState(false);
  const [favoriteChannels, setFavoriteChannels] = useState([]);
  const [loadingFavoriteChannels, setLoadingFavoriteChannels] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [filterByFavorites, setFilterByFavorites] = useState(
    () => searchParams.get('favorites') === 'true',
  );
  const [minViewCount, setMinViewCount] = useState(() => searchParams.get('minViewCount') || '');
  const [debouncedMinViewCount, setDebouncedMinViewCount] = useState(
    () => searchParams.get('minViewCount') || '',
  );
  const [togglingFavorites, setTogglingFavorites] = useState(new Set());
  const isUpdatingFromUserInput = React.useRef(false);

  // Helper function to update URL with all filter parameters
  const updateURLParams = React.useCallback(
    (updates) => {
      if (isUpdatingFromUserInput.current) {
        return;
      }

      const newSearchParams = new URLSearchParams(searchParams);
      let hasChanges = false;

      // Update each parameter and check if anything changed
      Object.entries(updates).forEach(([key, value]) => {
        const currentValue = newSearchParams.get(key);
        // Allow null to delete parameter, otherwise use the value as-is (including 'false' strings)
        const newValue = value === null ? null : String(value);

        if (newValue !== null) {
          if (currentValue !== newValue) {
            newSearchParams.set(key, newValue);
            hasChanges = true;
          }
        } else {
          if (currentValue !== null) {
            newSearchParams.delete(key);
            hasChanges = true;
          }
        }
      });

      // Only update URL if something actually changed
      if (hasChanges) {
        isUpdatingFromUserInput.current = true;
        setSearchParams(newSearchParams, { replace: true });

        // Reset flag after a brief delay
        setTimeout(() => {
          isUpdatingFromUserInput.current = false;
        }, 100);
      }
    },
    [searchParams, setSearchParams],
  );

  // Initialize debouncedQuery and debouncedMinViewCount from URL on mount
  useEffect(() => {
    const urlQuery = searchParams.get('q') || '';
    if (urlQuery) {
      setDebouncedQuery(urlQuery.trim());
    }
    const urlMinViewCount = searchParams.get('minViewCount') || '';
    if (urlMinViewCount) {
      setDebouncedMinViewCount(urlMinViewCount.trim());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle browser back/forward navigation - restore all filters from URL
  useEffect(() => {
    // Skip if we're updating from user input (to prevent loops)
    if (isUpdatingFromUserInput.current) {
      return;
    }

    const urlSortBy = searchParams.get('sortBy') || 'viewCount';
    const urlSortOrder = searchParams.get('sortOrder') || 'desc';
    const urlDateFilter = searchParams.get('dateFilter') || 'week';
    const urlQuery = searchParams.get('q') || '';
    const translationParam = searchParams.get('translation');
    const urlShowTranslation = translationParam !== null ? translationParam === 'true' : true;
    const urlFilterByFavorites = searchParams.get('favorites') === 'true';
    const urlMinViewCount = searchParams.get('minViewCount') || '';

    // Update states only if they differ from URL
    if (urlSortBy !== sortBy) setSortBy(urlSortBy);
    if (urlSortOrder !== sortOrder) setSortOrder(urlSortOrder);
    if (urlDateFilter !== dateFilter) setDateFilter(urlDateFilter);
    if (urlQuery !== query) {
      setQuery(urlQuery);
      setDebouncedQuery(urlQuery.trim());
    }
    if (urlShowTranslation !== showTranslation) setShowTranslation(urlShowTranslation);
    if (urlFilterByFavorites !== filterByFavorites) setFilterByFavorites(urlFilterByFavorites);
    if (urlMinViewCount !== minViewCount) {
      setMinViewCount(urlMinViewCount);
      setDebouncedMinViewCount(urlMinViewCount.trim());
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update URL when sortBy changes
  useEffect(() => {
    updateURLParams({ sortBy });
  }, [sortBy, updateURLParams]);

  // Update URL when sortOrder changes
  useEffect(() => {
    updateURLParams({ sortOrder });
  }, [sortOrder, updateURLParams]);

  // Update URL when dateFilter changes
  useEffect(() => {
    updateURLParams({ dateFilter });
  }, [dateFilter, updateURLParams]);

  // Update URL when showTranslation changes
  useEffect(() => {
    updateURLParams({ translation: showTranslation ? 'true' : 'false' });
  }, [showTranslation, updateURLParams]);

  // Update URL when filterByFavorites changes
  useEffect(() => {
    updateURLParams({ favorites: filterByFavorites ? 'true' : null });
  }, [filterByFavorites, updateURLParams]);

  // Debounce minViewCount to avoid fetching on each keystroke and update URL
  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmedMinViewCount = minViewCount.trim();
      setDebouncedMinViewCount(trimmedMinViewCount);
      updateURLParams({ minViewCount: trimmedMinViewCount || null });
    }, 1000);
    return () => clearTimeout(handle);
  }, [minViewCount, updateURLParams]);

  // Debounce query to avoid fetching on each keystroke and update URL
  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmedQuery = query.trim();
      setDebouncedQuery(trimmedQuery);
      updateURLParams({ q: trimmedQuery || null });
    }, 1000);
    return () => clearTimeout(handle);
  }, [query, updateURLParams]);

  const searchSnapshot = React.useMemo(
    () => ({
      query: debouncedQuery,
      sortBy,
      order: sortOrder,
      dateFilter,
      searchOnTranslation: showTranslation,
      favorites: filterByFavorites,
      minViewCount: debouncedMinViewCount,
      folderId: id,
      folderName: folder?.name,
    }),
    [
      debouncedQuery,
      sortBy,
      sortOrder,
      dateFilter,
      showTranslation,
      filterByFavorites,
      debouncedMinViewCount,
      id,
      folder?.name,
    ],
  );

  const folderOpenTracked = React.useRef(null);
  useEffect(() => {
    if (!folder || !id) return;
    if (folderOpenTracked.current === id) return;
    folderOpenTracked.current = id;
    trackEvent('folder_open', {
      scope: 'folder',
      context: { folderId: id, folderName: folder.name },
      payload: { folderId: id, folderName: folder.name },
    });
  }, [folder, id]);

  const fetchFolderData = async (isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError('');
      }

      let folderNameForAnalytics = folder?.name;

      // Fetch folder details (only on initial load)
      if (!isLoadMore) {
        const folderResponse = await axios.get(`${API_URL}/api/folders/${id}`);
        const folderData = folderResponse.data;
        setFolder(folderData);
        folderNameForAnalytics = folderData.name;

        // Fetch channel count in this folder (efficient count-only query)
        const channelsResponse = await axios.get(
          `${API_URL}/api/folders/${id}/channels?countOnly=true`,
        );
        const channelsData = channelsResponse.data;
        setChannelCount(channelsData.count || 0);
      }

      // Fetch videos in this folder with pagination
      const offset = isLoadMore ? videos.length : 0;
      const params = new URLSearchParams({
        sortBy,
        order: sortOrder,
        offset: String(offset),
        limit: String(50),
        dateFilter,
      });
      if (debouncedQuery && debouncedQuery.length > 0) {
        params.set('query', debouncedQuery);
      }
      if (showTranslation) {
        params.set('searchOnTranslation', 'true');
      }
      if (filterByFavorites) {
        params.set('favorites', 'true');
      }
      if (debouncedMinViewCount && debouncedMinViewCount.trim()) {
        params.set('minViewCount', debouncedMinViewCount.trim());
      }

      const videosResponse = await axios.get(
        `${API_URL}/api/folders/${id}/videos?${params.toString()}`,
      );
      const videosData = videosResponse.data;

      if (isLoadMore) {
        setVideos((prev) => [...prev, ...videosData.videos]);
      } else {
        setVideos(videosData.videos);
      }

      setHasMore(videosData.hasMore);
      setTotalVideos(videosData.total);

      if (isLoadMore) {
        trackEvent('load_more', {
          scope: 'folder',
          context: { folderId: id, folderName: folderNameForAnalytics },
          payload: {
            ...searchSnapshot,
            folderName: folderNameForAnalytics,
            offset: videos.length,
          },
        });
      } else {
        trackEvent('search', {
          scope: 'folder',
          context: { folderId: id, folderName: folderNameForAnalytics },
          payload: {
            ...searchSnapshot,
            folderName: folderNameForAnalytics,
            total: videosData.total,
          },
        });
      }
    } catch (error) {
      console.error('Error fetching folder data:', error);
      setError(error.message || 'Failed to load folder');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreVideos = React.useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchFolderData(true);
    }
  }, [loadingMore, hasMore]); // eslint-disable-line react-hooks/exhaustive-deps

  // Intersection Observer for infinite scrolling
  const observerRef = React.useRef();
  const loadMoreRef = React.useCallback(
    (node) => {
      if (loadingMore || !hasMore) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          loadMoreVideos();
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [loadingMore, hasMore, loadMoreVideos],
  );

  useEffect(() => {
    // Reset videos when sort parameters change
    setVideos([]);
    setHasMore(false);
    setTotalVideos(0);
    fetchFolderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, sortBy, sortOrder, dateFilter, debouncedQuery, filterByFavorites, debouncedMinViewCount]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key.toLowerCase() === 'z') {
        setShowBulkAnalyze(true);
      }
    };

    const handleKeyUp = (event) => {
      if (event.key.toLowerCase() === 'z') {
        setShowBulkAnalyze(false);
      }
    };

    const handleWindowBlur = () => {
      setShowBulkAnalyze(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  const handleAddChannels = async () => {
    if (!channelInput.trim()) {
      return;
    }

    setAddingChannels(true);
    setStatusMessages([]);

    // Parse the input - split by newlines or commas
    const channelUrls = channelInput
      .split(/[\n,]+/)
      .map((url) => url.trim())
      .filter((url) => url.length > 0);

    if (channelUrls.length === 0) {
      setAddingChannels(false);
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/tracked-channels`, {
        channelUrls,
        folderId: id,
      });

      const data = response.data;

      // Display status messages for each result
      const messages = data.results.map((result, index) => {
        if (result.success) {
          return {
            type: 'success',
            text: `✓ Added: ${result.channelId}`,
          };
        } else {
          return {
            type: 'error',
            text: `✗ Failed: ${result.error} (${result.input})`,
          };
        }
      });

      setStatusMessages(messages);

      trackEvent('add_channel', {
        scope: 'folder',
        context: { folderId: id, folderName: folder?.name },
        payload: {
          folderId: id,
          folderName: folder?.name,
          channelUrlCount: channelUrls.length,
        },
      });

      // Clear input
      setChannelInput('');
    } catch (error) {
      console.error('Error adding channels:', error);
      setStatusMessages([
        {
          type: 'error',
          text: `Error: ${error.message}`,
        },
      ]);
    } finally {
      setAddingChannels(false);
    }
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setChannelInput('');
    setStatusMessages([]);
    // Refresh folder data when closing dialog
    fetchFolderData();
  };

  const handleRemoveChannelClick = (channelId) => {
    setChannelToRemove(channelId);
    setRemoveDialogOpen(true);
  };

  const handleRemoveChannelConfirm = async () => {
    if (!channelToRemove) return;

    setRemovingChannel(true);
    try {
      await axios.delete(`${API_URL}/api/folders/${id}/channels/${channelToRemove}`);

      trackEvent('remove_channel', {
        scope: 'folder',
        context: { folderId: id, folderName: folder?.name },
        payload: { folderId: id, channelId: channelToRemove },
      });

      // Close dialog and reset state
      setRemoveDialogOpen(false);
      setChannelToRemove(null);

      // Refresh data after successful removal
      setSuccess('Channel removed from folder successfully!');
      setTimeout(() => setSuccess(''), 3000);

      // Reset and refetch all data
      setVideos([]);
      await fetchFolderData();

      // Refresh channels list if modal is open
      if (channelsModalOpen) {
        await fetchChannels();
      }
    } catch (error) {
      console.error('Error removing channel from folder:', error);
      setError(error.message || 'Failed to remove channel from folder');
      setTimeout(() => setError(''), 5000);
      setRemoveDialogOpen(false);
      setChannelToRemove(null);
    } finally {
      setRemovingChannel(false);
    }
  };

  const handleRemoveDialogClose = () => {
    if (!removingChannel) {
      setRemoveDialogOpen(false);
      setChannelToRemove(null);
    }
  };

  const fetchChannels = async () => {
    setLoadingChannels(true);
    try {
      const response = await axios.get(`${API_URL}/api/folders/${id}/channels`);
      setChannels(response.data);
    } catch (error) {
      console.error('Error fetching channels:', error);
      setError(error.message || 'Failed to load channels');
    } finally {
      setLoadingChannels(false);
    }
  };

  const fetchFavoriteChannels = async () => {
    setLoadingFavoriteChannels(true);
    try {
      const response = await axios.get(`${API_URL}/api/folders/${id}/channels?favorites=true`);
      setFavoriteChannels(response.data);
    } catch (error) {
      console.error('Error fetching favorite channels:', error);
      setError(error.message || 'Failed to load favorite channels');
    } finally {
      setLoadingFavoriteChannels(false);
    }
  };

  const handleChannelsModalOpen = () => {
    setChannelsModalOpen(true);
    setShowFavorites(false);
    fetchChannels();
  };

  const handleChannelsModalClose = () => {
    setChannelsModalOpen(false);
    setChannels([]);
    setFavoriteChannels([]);
    setShowFavorites(false);
    setChannelStatusFilter('all');
  };

  const handleToggleFavoritesView = () => {
    const newShowFavorites = !showFavorites;
    setShowFavorites(newShowFavorites);
    if (newShowFavorites) {
      fetchFavoriteChannels();
    } else {
      fetchChannels();
    }
  };

  const handleCopyAllChannelUrls = async () => {
    const channelsToCopy = showFavorites ? favoriteChannels : channels;
    const channelUrls = channelsToCopy
      .map((channel) => `https://www.youtube.com/channel/${channel.channelId}`)
      .join('\n');

    try {
      await navigator.clipboard.writeText(channelUrls);
      setCopyTooltipOpen(true);
      setTimeout(() => setCopyTooltipOpen(false), 2000);
    } catch (error) {
      console.error('Failed to copy channel URLs:', error);
      setError('Failed to copy channel URLs');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleBulkAnalyzeOpen = () => {
    setBulkAnalyzeModalOpen(true);
  };

  const handleBulkAnalyzeClose = () => {
    setBulkAnalyzeModalOpen(false);
  };

  const handleToggleChannelFavorite = async (channelId, currentFavoriteStatus) => {
    try {
      setTogglingFavorites((prev) => new Set(prev).add(channelId));
      const newFavoriteStatus = !currentFavoriteStatus;

      await axios.put(`${API_URL}/api/channels/${channelId}/favorite`, {
        isFavorite: newFavoriteStatus,
      });

      // Update the channel in the local state
      const updateChannelFavorite = (channelList) =>
        channelList.map((channel) =>
          channel.channelId === channelId ? { ...channel, isFavorite: newFavoriteStatus } : channel,
        );

      setChannels(updateChannelFavorite);
      setFavoriteChannels(updateChannelFavorite);

      // If we're in favorites view and removing favorite, refresh the list
      if (showFavorites && !newFavoriteStatus) {
        await fetchFavoriteChannels();
      }

      setSuccess(
        newFavoriteStatus ? 'Channel added to favorites' : 'Channel removed from favorites',
      );
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error toggling favorite:', error);
      setError('Failed to update favorite status');
      setTimeout(() => setError(''), 5000);
    } finally {
      setTogglingFavorites((prev) => {
        const next = new Set(prev);
        next.delete(channelId);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <StyledContainer>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </StyledContainer>
      </Container>
    );
  }

  if (error || !folder) {
    return (
      <Container maxWidth="xl">
        <StyledContainer>
          <StyledPaper>
            <Alert severity="error">{error || 'Folder not found'}</Alert>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate('/');
                }
              }}
              sx={{ mt: 2 }}
            >
              Back
            </Button>
          </StyledPaper>
        </StyledContainer>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <StyledContainer>
        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
            {success}
          </Alert>
        )}

        <StyledPaper>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => {
                  if (window.history.length > 1) {
                    navigate(-1);
                  } else {
                    navigate('/');
                  }
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                Back
              </Button>
              <StyledSectionHeaderWithBadge>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <StyledAnalyticsIconBox>
                    <YouTube />
                  </StyledAnalyticsIconBox>
                  <Typography variant="h5" fontWeight={600}>
                    {folder.name}
                  </Typography>
                </Box>
              </StyledSectionHeaderWithBadge>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {(videos.some((video) => video.translation) ||
                videos.some((video) => video.channelNameTranslation)) && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={showTranslation}
                      onChange={(e) => setShowTranslation(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#6366f1',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#6366f1',
                        },
                        '& .MuiSwitch-track': {
                          backgroundColor: 'rgba(148, 163, 184, 0.3)',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Translate sx={{ fontSize: 18 }} />
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        Translation
                      </Typography>
                    </Box>
                  }
                  sx={{
                    margin: 0,
                    '& .MuiFormControlLabel-label': {
                      color: 'text.primary',
                    },
                  }}
                />
              )}
              {showBulkAnalyze && (
                <Tooltip
                  title="Generate Video Summaries, Analyzing Writing Styles, And More"
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: { textAlign: 'center' },
                    },
                  }}
                >
                  <span>
                    <Button
                      variant="contained"
                      startIcon={<Analytics />}
                      onClick={handleBulkAnalyzeOpen}
                      disabled={videos.length === 0}
                      sx={{
                        borderRadius: 3,
                        padding: '8px 20px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
                        '&:hover': {
                          boxShadow: '0 12px 48px rgba(16, 185, 129, 0.4)',
                        },
                        '&:disabled': {
                          background: 'rgba(148, 163, 184, 0.3)',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      Bulk Analyze
                    </Button>
                  </span>
                </Tooltip>
              )}
              <StyledChannelChip
                label={`${channelCount} Channels`}
                onClick={handleChannelsModalOpen}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.9,
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              />
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  setAddDialogOpen(true);
                  setStatusMessages([]);
                  setChannelInput('');
                }}
                sx={{
                  borderRadius: 3,
                  padding: '8px 20px',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)',
                  '&:hover': {
                    boxShadow: '0 12px 48px rgba(99, 102, 241, 0.4)',
                  },
                }}
              >
                Add Channels
              </Button>
            </Box>
          </Box>

          {/* Sorting & Search Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Sort By</InputLabel>
              <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value)}>
                <MenuItem value="publishedTime">Published Date</MenuItem>
                <MenuItem value="outlierScore">Outlier Score</MenuItem>
                <MenuItem value="viewCount">View Count</MenuItem>
                <MenuItem value="viewYesterday">View Yesterday</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Order</InputLabel>
              <Select
                value={sortOrder}
                label="Order"
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <MenuItem value="desc">Descending</MenuItem>
                <MenuItem value="asc">Ascending</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Published Date</InputLabel>
              <Select
                value={dateFilter}
                label="Published Date"
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="week">Last Week</MenuItem>
                <MenuItem value="2weeks">Last 2 Weeks</MenuItem>
                <MenuItem value="3weeks">Last 3 Weeks</MenuItem>
                <MenuItem value="month">Last Month</MenuItem>
                <MenuItem value="2months">Last 2 Months</MenuItem>
                <MenuItem value="3months">Last 3 Months</MenuItem>
                <MenuItem value="6months">Last 6 Months</MenuItem>
                <MenuItem value="year">Last Year</MenuItem>
                <MenuItem value="3years">Last 3 Years</MenuItem>
                <MenuItem value="5years">Last 5 Years</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="number"
              placeholder="Min Views"
              value={minViewCount}
              onChange={(e) => setMinViewCount(e.target.value)}
              sx={{
                width: 120,
                maxWidth: 120,
                '& input[type=number]': {
                  MozAppearance: 'textfield',
                },
                '& input[type=number]::-webkit-outer-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
                '& input[type=number]::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
              }}
              inputProps={{ min: 0 }}
            />
            <TextField
              size="small"
              placeholder="Search title"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <Tooltip
              title={filterByFavorites ? 'Show all channels' : 'Filter by favorite channels'}
            >
              <IconButton
                onClick={() => setFilterByFavorites(!filterByFavorites)}
                sx={{
                  color: filterByFavorites ? '#fbbf24' : 'text.secondary',
                  '&:hover': {
                    color: '#fbbf24',
                    backgroundColor: 'rgba(251, 191, 36, 0.1)',
                  },
                }}
              >
                {filterByFavorites ? <Star /> : <StarBorder />}
              </IconButton>
            </Tooltip>
            <Chip
              label={`${videos.length}${totalVideos > 0 ? ` / ${totalVideos}` : ''} Videos (${
                {
                  all: 'All Time',
                  week: 'Last Week',
                  '2weeks': 'Last 2 Weeks',
                  '3weeks': 'Last 3 Weeks',
                  month: 'Last Month',
                  '2months': 'Last 2 Months',
                  '3months': 'Last 3 Months',
                  '6months': 'Last 6 Months',
                  year: 'Last Year',
                }[dateFilter] || 'All Time'
              })`}
              sx={{ ml: 'auto' }}
            />
          </Box>

          {channelCount === 0 ? (
            <StyledEmptyState>
              <StyledEmptyStateIcon>
                <YouTube sx={{ fontSize: 40, color: 'text.secondary' }} />
              </StyledEmptyStateIcon>
              <Typography variant="h6" color="text.secondary" mb={1}>
                No channels in this folder yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add channels to start tracking
              </Typography>
            </StyledEmptyState>
          ) : videos.length === 0 ? (
            <StyledEmptyState>
              <StyledEmptyStateIcon>
                <VideoLibrary sx={{ fontSize: 40, color: 'text.secondary' }} />
              </StyledEmptyStateIcon>
              <Typography variant="h6" color="text.secondary" mb={1}>
                No videos yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Videos will appear after they are fetched from tracked channels
              </Typography>
            </StyledEmptyState>
          ) : (
            <Grid container spacing={2} sx={{ mt: 2, justifyContent: 'center' }}>
              {videos.map((video) => (
                <Grid item key={video._id} sx={{ width: '24%', flexBasis: '24%', maxWidth: '24%' }}>
                  <VideoCard
                    video={video}
                    highlightQuery={debouncedQuery}
                    onRemoveChannel={handleRemoveChannelClick}
                    showTranslation={showTranslation}
                    sortBy={sortBy}
                    analyticsContext={{
                      scope: 'folder',
                      folderId: id,
                      folderName: folder?.name,
                      channelId: video.channelId,
                      channelTitle: video.channelName,
                      searchSnapshot,
                    }}
                  />
                </Grid>
              ))}
              {!hasMore && videos.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      All videos loaded
                    </Typography>
                  </Box>
                </Grid>
              )}
              {hasMore && (
                <Grid item xs={12} ref={loadMoreRef}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    {loadingMore && <CircularProgress size={24} />}
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </StyledPaper>
      </StyledContainer>

      {/* Add Channels Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background:
              'linear-gradient(145deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
            border: '1px solid rgba(148, 163, 184, 0.1)',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Add />
            <Typography variant="h6">Add Channels to "{folder?.name}"</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box component="form" onSubmit={(e) => e.preventDefault()}>
            <StyledTextField
              id="channel-input"
              label="Channel URLs or Handles"
              multiline
              rows={6}
              fullWidth
              value={channelInput}
              onChange={(e) => setChannelInput(e.target.value)}
              placeholder="Paste channel URLs here (one per line or comma-separated)
Examples:
https://www.youtube.com/@channelhandle
https://www.youtube.com/channel/UC...
https://www.youtube.com/c/channelname"
            />
          </Box>

          {statusMessages.length > 0 && (
            <Box sx={{ mt: 3 }}>
              {statusMessages.map((msg, index) => (
                <Alert
                  key={index}
                  severity={msg.type === 'success' ? 'success' : 'error'}
                  icon={msg.type === 'success' ? <CheckCircle /> : <ErrorIcon />}
                  sx={{
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                    mb: 2,
                  }}
                >
                  {msg.text}
                </Alert>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseAddDialog} disabled={addingChannels}>
            Close
          </Button>
          {statusMessages.some((msg) => msg.type === 'success') && !addingChannels && (
            <Button
              onClick={handleCloseAddDialog}
              variant="contained"
              startIcon={<Folder />}
              sx={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  opacity: 0.9,
                },
              }}
            >
              Go to Folder
            </Button>
          )}
          <Button
            onClick={handleAddChannels}
            variant="contained"
            disabled={addingChannels || !channelInput.trim()}
            startIcon={
              addingChannels ? <CircularProgress size={20} color="inherit" /> : <PlaylistAddCheck />
            }
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                opacity: 0.9,
              },
            }}
          >
            {addingChannels ? 'Adding...' : 'Add Channels'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Channel Confirmation Dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={handleRemoveDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background:
              'linear-gradient(145deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '50%',
                background:
                  'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.2) 100%)',
                border: '2px solid rgba(239, 68, 68, 0.5)',
              }}
            >
              <DeleteIcon sx={{ fontSize: 28, color: '#ef4444' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Remove Channel from Folder
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText
            sx={{
              color: 'text.primary',
              fontSize: '1rem',
              lineHeight: 1.6,
            }}
          >
            Are you sure you want to remove this channel from the folder? This will remove all
            videos from this channel from the folder view.
          </DialogContentText>
          <Alert
            severity="info"
            sx={{
              mt: 2,
              borderRadius: 2,
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
            }}
          >
            <Typography variant="body2">
              <strong>Note:</strong> The channel and its tracked data will remain in the system.
              Only the folder association will be removed.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleRemoveDialogClose}
            disabled={removingChannel}
            sx={{
              borderRadius: 2,
              px: 3,
              color: 'text.secondary',
              '&:hover': {
                background: 'rgba(148, 163, 184, 0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveChannelConfirm}
            variant="contained"
            disabled={removingChannel}
            startIcon={
              removingChannel ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />
            }
            sx={{
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              boxShadow: '0 8px 32px rgba(239, 68, 68, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                boxShadow: '0 12px 48px rgba(239, 68, 68, 0.4)',
              },
              '&:disabled': {
                background: 'rgba(239, 68, 68, 0.5)',
              },
            }}
          >
            {removingChannel ? 'Removing...' : 'Remove Channel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Channels List Modal */}
      <Dialog
        open={channelsModalOpen}
        onClose={handleChannelsModalClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background:
              'linear-gradient(145deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
            border: '1px solid rgba(148, 163, 184, 0.1)',
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {showFavorites ? <Star /> : <People />}
              <Typography variant="h6">
                {showFavorites ? 'Favorite Channels' : `Channels in "${folder?.name}"`} (
                {(() => {
                  const channelsToCount = showFavorites ? favoriteChannels : channels;
                  if (channelStatusFilter === 'all') {
                    return showFavorites ? favoriteChannels.length : channelCount;
                  }
                  return channelsToCount.filter((channel) => {
                    return channel.channelData?.isTerminated === true;
                  }).length;
                })()}
                )
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Tooltip title={showFavorites ? 'Show all channels' : 'Show favorite channels'}>
                <IconButton
                  onClick={handleToggleFavoritesView}
                  sx={{
                    color: showFavorites ? '#fbbf24' : 'text.secondary',
                    '&:hover': {
                      color: '#fbbf24',
                      backgroundColor: 'rgba(251, 191, 36, 0.1)',
                    },
                  }}
                >
                  <Star />
                </IconButton>
              </Tooltip>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={channelStatusFilter}
                  label="Status"
                  onChange={(e) => setChannelStatusFilter(e.target.value)}
                  sx={{
                    color: 'text.primary',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(148, 163, 184, 0.3)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(148, 163, 184, 0.5)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1',
                    },
                  }}
                >
                  <MenuItem value="all">All Channels</MenuItem>
                  <MenuItem value="terminated">Terminated</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={channelSortBy}
                  label="Sort By"
                  onChange={(e) => setChannelSortBy(e.target.value)}
                  sx={{
                    color: 'text.primary',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(148, 163, 184, 0.3)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(148, 163, 184, 0.5)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1',
                    },
                  }}
                >
                  <MenuItem value="totalViews">Total Views</MenuItem>
                  <MenuItem value="viewsLast3Days">Views Last 3 Days</MenuItem>
                  <MenuItem value="subscribers">Subscribers</MenuItem>
                </Select>
              </FormControl>
              <Tooltip open={copyTooltipOpen} title="Copied!" placement="top" arrow>
                <Button
                  startIcon={<ContentCopy />}
                  onClick={handleCopyAllChannelUrls}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'text.primary',
                      background: 'rgba(148, 163, 184, 0.1)',
                    },
                  }}
                >
                  Copy URLs
                </Button>
              </Tooltip>
              <IconButton
                onClick={handleChannelsModalClose}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary',
                    background: 'rgba(148, 163, 184, 0.1)',
                  },
                }}
              >
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 0 }}>
          {(showFavorites ? loadingFavoriteChannels : loadingChannels) ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            (() => {
              // Filter channels by status
              let channelsToDisplay = showFavorites ? favoriteChannels : channels;

              if (channelStatusFilter === 'terminated') {
                channelsToDisplay = channelsToDisplay.filter((channel) => {
                  return channel.channelData?.isTerminated === true;
                });
              }

              // Sort channels by selected option (descending)
              channelsToDisplay = [...channelsToDisplay].sort((a, b) => {
                let aValue = 0;
                let bValue = 0;

                if (channelSortBy === 'totalViews') {
                  aValue = a.channelData?.totalViewCount ?? 0;
                  bValue = b.channelData?.totalViewCount ?? 0;
                } else if (channelSortBy === 'viewsLast3Days') {
                  aValue = a.channelData?.viewsLast3Days ?? 0;
                  bValue = b.channelData?.viewsLast3Days ?? 0;
                } else if (channelSortBy === 'subscribers') {
                  aValue = a.channelData?.subscriberCount ?? 0;
                  bValue = b.channelData?.subscriberCount ?? 0;
                }

                return bValue - aValue; // Descending order
              });

              // Show empty state if no channels match the filter
              if (channelsToDisplay.length === 0) {
                return (
                  <StyledEmptyState>
                    <StyledEmptyStateIcon>
                      {showFavorites ? (
                        <Star sx={{ fontSize: 40, color: 'text.secondary' }} />
                      ) : (
                        <People sx={{ fontSize: 40, color: 'text.secondary' }} />
                      )}
                    </StyledEmptyStateIcon>
                    <Typography variant="h6" color="text.secondary" mb={1}>
                      {channelStatusFilter === 'terminated'
                        ? 'No terminated channels found'
                        : showFavorites
                          ? 'No favorite channels found'
                          : 'No channels found'}
                    </Typography>
                    {showFavorites && channelStatusFilter === 'all' && (
                      <Typography variant="body2" color="text.secondary">
                        Favorite channels from this folder will appear here
                      </Typography>
                    )}
                  </StyledEmptyState>
                );
              }

              return (
                <List sx={{ maxHeight: '75vh', overflow: 'auto', py: 0, pb: 0 }}>
                  {channelsToDisplay.map((channel, index) => (
                    <React.Fragment key={channel._id || channel.channelId}>
                      <ListItem
                        component="a"
                        href={`/channels/${channel.channelId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          py: 1,
                          px: 2,
                          borderRadius: 2,
                          mb: 0.5,
                          background: 'rgba(148, 163, 184, 0.05)',
                          cursor: 'pointer',
                          textDecoration: 'none',
                          color: 'inherit',
                          position: 'relative',
                          '&:hover': {
                            background: 'rgba(148, 163, 184, 0.15)',
                          },
                          transition: 'background-color 0.2s ease-in-out',
                        }}
                      >
                        <ListItemAvatar sx={{ mr: 2 }}>
                          <Avatar
                            src={channel.avatarUrl}
                            sx={{
                              width: 56,
                              height: 56,
                              background: 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
                            }}
                          >
                            <YouTube />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600, color: 'text.primary' }}
                              >
                                {channel.channelData?.channelName || channel.channelId}
                              </Typography>
                              {(() => {
                                if (channel.channelData?.isTerminated === true) {
                                  return (
                                    <Tooltip title="Terminated">
                                      <Cancel
                                        sx={{
                                          fontSize: 20,
                                          color: '#ef4444',
                                        }}
                                      />
                                    </Tooltip>
                                  );
                                }
                                return null;
                              })()}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 1.5,
                                  alignItems: 'center',
                                }}
                              >
                                {channel.channelData?.subscriberCount !== undefined && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'text.secondary',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {channel.channelData.subscriberCount.toLocaleString()}{' '}
                                    subscribers
                                  </Typography>
                                )}
                                {channel.channelData?.totalViewCount !== undefined && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'text.secondary',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {channel.channelData.totalViewCount.toLocaleString()} total
                                    views
                                  </Typography>
                                )}
                                {channel.channelData?.viewsLast3Days !== undefined &&
                                  channel.channelData.viewsLast3Days !== null && (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: 'text.secondary',
                                        fontWeight: 500,
                                      }}
                                    >
                                      {channel.channelData.viewsLast3Days.toLocaleString()} views
                                      (3d)
                                    </Typography>
                                  )}
                                {channel.channelData?.viewsLast7Days !== undefined &&
                                  channel.channelData.viewsLast7Days !== null && (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: 'text.secondary',
                                        fontWeight: 500,
                                      }}
                                    >
                                      {channel.channelData.viewsLast7Days.toLocaleString()} views
                                      (7d)
                                    </Typography>
                                  )}
                                {channel.channelData?.viewsLast30Days !== undefined &&
                                  channel.channelData.viewsLast30Days !== null && (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: 'text.secondary',
                                        fontWeight: 500,
                                      }}
                                    >
                                      {channel.channelData.viewsLast30Days.toLocaleString()} views
                                      (30d)
                                    </Typography>
                                  )}
                                {channel.channelData?.videoCount !== undefined && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'text.secondary',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {channel.channelData.videoCount.toLocaleString()} videos
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          }
                        />
                        {!showFavorites && (
                          <Tooltip title="Remove from folder">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleRemoveChannelClick(channel.channelId);
                              }}
                              sx={{
                                color: 'text.secondary',
                                mr: 1,
                                '&:hover': {
                                  color: '#ef4444',
                                  background: 'rgba(239, 68, 68, 0.1)',
                                },
                                transition: 'all 0.2s ease-in-out',
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip
                          title={channel.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleToggleChannelFavorite(
                                channel.channelId,
                                channel.isFavorite || false,
                              );
                            }}
                            disabled={togglingFavorites.has(channel.channelId)}
                            sx={{
                              color: channel.isFavorite ? '#fbbf24' : 'text.secondary',
                              mr: 1,
                              '&:hover': {
                                color: '#fbbf24',
                                background: 'rgba(251, 191, 36, 0.1)',
                              },
                              '&:disabled': {
                                opacity: 0.5,
                              },
                              transition: 'all 0.2s ease-in-out',
                            }}
                          >
                            {togglingFavorites.has(channel.channelId) ? (
                              <CircularProgress size={20} />
                            ) : channel.isFavorite ? (
                              <Star />
                            ) : (
                              <StarBorder />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Open YouTube Channel">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(
                                `https://www.youtube.com/channel/${channel.channelId}`,
                                '_blank',
                                'noopener,noreferrer',
                              );
                            }}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': {
                                color: '#ff0000',
                                background: 'rgba(255, 0, 0, 0.1)',
                              },
                              transition: 'all 0.2s ease-in-out',
                            }}
                          >
                            <YouTube />
                          </IconButton>
                        </Tooltip>
                      </ListItem>
                      {index < channelsToDisplay.length - 1 && <Divider sx={{ my: 0.5 }} />}
                    </React.Fragment>
                  ))}
                </List>
              );
            })()
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Analyze Modal */}
      <BulkAnalyzeModal
        open={bulkAnalyzeModalOpen}
        onClose={handleBulkAnalyzeClose}
        videos={videos}
        showTranslation={showTranslation}
        onSuccess={(message) => {
          setSuccess(message);
          setTimeout(() => setSuccess(''), 5000);
        }}
        onError={(message) => {
          setError(message);
          setTimeout(() => setError(''), 5000);
        }}
        onRefresh={fetchFolderData}
      />
    </Container>
  );
}

export default FolderDetail;
